import { useEffect, useMemo, useState } from "react";
import { Check, Loader2, TriangleAlert, ArrowRight } from "lucide-react";

import { type Message } from "shared/apiClient";
import type { QuestionData } from "@/components/wizard/lib";

import { createMessageEvent, useChannel } from "shared/channel";
import { useAccount, useCaptureEvent, CaptureEventType } from "@/hooks";

import { Button, Slider } from "shared/components";
import { QuestionLoader } from "@/components/wizard/questionLoader/QuestionLoader";

import tpzDiagram from "@/assets/tpz-diagram.png";

export interface TreeDistanceStepProps {
  disabled?: boolean;
  message: Message;
  showLoading?: boolean;
}

export function TreeDistanceStep({
  disabled,
  message,
  showLoading,
}: TreeDistanceStepProps) {
  const { captureEvent } = useCaptureEvent();

  const questionData = message.question_data as QuestionData;

  const { channel } = useChannel();

  const { conversation } = useAccount();

  const [submitting, setSubmitting] = useState(false);
  const [treeDiameter, setTreeDiameter] = useState(0.3);
  const [treeDistance, setTreeDistance] = useState(5);

  const protectionZoneDistance = useMemo(() => {
    return treeDiameter * 12;
  }, [treeDiameter]);

  useEffect(() => {
    const diameter = window.localStorage.getItem(
      `${message.conversation_id}.treeDiameter`,
    );
    setTreeDiameter(diameter ? Number(diameter) : 0.3);
  }, [message]);

  const handleClickNext = () => {
    if (channel && conversation?.id) {
      setSubmitting(true);

      const text = `The tree is ${treeDistance.toFixed(1)} meters away from the proposed works.`;

      const messageDescriptor = createMessageEvent({
        conversation_id: conversation.id,
        text,
      });

      console.log("<TreeDistanceStep> handleClickNext()", messageDescriptor);
      channel.sendMessage(messageDescriptor);

      captureEvent(CaptureEventType.USER_ANSWERED_QUESTION, {
        messageId: message.id,
      });
    }
  };

  const renderDiagram = () => {
    return (
      <img src={tpzDiagram} width={180} className="h-auto" alt="Tpz diagram" />
    );
  };

  const renderDescription = () => {
    return (
      <>
        <p className="text-textSubdued text-bodylg">
          Based on the{" "}
          <b>
            tree's diameter of breast height of {treeDiameter.toFixed(1)} metres
          </b>
          , we've calculated a{" "}
          <b>
            protection zone around the tree of about{" "}
            {protectionZoneDistance.toFixed(1)} metres
          </b>
          .
        </p>
        <p className="text-textSubdued text-bodylg">
          If your proposal encroaches on the protection zone, the tree's root
          system could be at risk during construction, and more planning
          requirements will likely apply to your proposal.
        </p>
      </>
    );
  };

  const renderFooter = () => {
    return (
      <>
        <Button
          className="wizard-step-hide"
          data-testid="next-button"
          disabled={submitting || showLoading || disabled}
          onClick={handleClickNext}
        >
          {submitting || showLoading ? (
            <>
              <Loader2 className="mr-3 animate-spin" data-testid="submitting" />
            </>
          ) : null}
          {questionData.next_button_label || "Next"}
          <ArrowRight className="ml-2" size={16} />
        </Button>
      </>
    );
  };

  if (showLoading) {
    return <QuestionLoader className="" />;
  }

  return (
    <div
      className="wizard-step-transition flex w-full grow flex-col gap-4 md:w-[600px] md:max-w-[600px]"
      data-testid="step-container-tree-distance"
    >
      <div className="bg-interfaceBgDefault flex min-h-[401px] min-w-[342px] items-center justify-center rounded-xl p-6">
        {renderDiagram()}
      </div>

      <h2 className="text-textPrimary text-headingxl mb-2">
        {questionData.question_title}
      </h2>

      {renderDescription()}

      <div className="border-blueDefault flex flex-col gap-4 rounded-lg border px-4 py-5">
        <div className="flex items-center gap-2">
          <Slider
            className=""
            defaultValue={[8]}
            disabled={submitting || showLoading || disabled}
            max={15}
            min={1}
            onValueChange={(value) => {
              setTreeDistance(value[0]);
            }}
            step={1}
            value={[treeDistance]}
          />
          <div className="w-[20px]">{treeDistance}m</div>
        </div>
        {treeDistance >= protectionZoneDistance ? (
          <div className="bg-successBgDefault text-successText flex gap-1 rounded px-2 py-1">
            <Check />
            Outside tree protection zone
          </div>
        ) : (
          <div className="bg-warningBgDefault text-warningText flex gap-1 rounded px-2 py-1">
            <TriangleAlert />
            Within tree protection zone
          </div>
        )}

        <div className="flex justify-end">{renderFooter()}</div>
      </div>
    </div>
  );
}
